import { Link } from '@inertiajs/react'
import Seo from '../components/Seo'
import { useShared } from '../lib/hooks'

interface Faq {
  q: string
  a: string // plain-text answer (also used for FAQPage JSON-LD)
}

const INSTALL_STEPS = [
  'Unzip the download if needed, so you have the pack’s .SerumPack file.',
  'Open Serum 2 on a track in your DAW (Ableton, Logic, FL Studio, etc.).',
  'Drag the .SerumPack file straight onto the Serum 2 window — Serum 2 imports the presets automatically.',
  'Alternatively, open Serum 2’s Menu (the ☰ button in the top bar) and choose the option to open/import a pack, then select the .SerumPack file.',
  'Open the preset browser — the new presets appear grouped under the pack’s name. That’s it.',
]

const FAQS: Faq[] = [
  {
    q: 'How do I install a preset pack in Serum 2?',
    a: 'Our packs ship as a .SerumPack file. Open Serum 2 in your DAW and drag the .SerumPack file directly onto the Serum 2 window — it imports automatically. Or use Serum 2’s Menu (☰) to open/import the pack. The presets then appear in the preset browser under the pack’s name. Make sure you’re on the latest version of Serum 2.',
  },
  {
    q: 'Where do I download my packs after buying?',
    a: 'Right after checkout you’re taken to a confirmation page, and your packs are always available under your account at /account. Sign in any time to re-download — your purchases never expire.',
  },
  {
    q: 'What do I need to use these packs?',
    a: 'Xfer Records Serum 2, updated to the latest version. The packs are made for Serum 2 and use the .SerumPack format. They work on macOS and Windows in any DAW that hosts Serum 2.',
  },
  {
    q: 'Do these work in the original Serum (Serum 1)?',
    a: 'These packs are built for Serum 2 and use the .SerumPack format, which Serum 1 does not support. We recommend updating to Serum 2.',
  },
  {
    q: 'I didn’t get my files, or the import isn’t working — what do I do?',
    a: 'First, sign in and re-download from your account at /account. If the .SerumPack still won’t import, confirm Serum 2 is updated to the latest version and try dragging the file onto the plugin again. Still stuck? Email support@formasound.com with your order email and we’ll sort it out.',
  },
  {
    q: 'What is your refund policy?',
    a: 'Because preset packs are digital products delivered instantly, all sales are generally final. If a pack is faulty or you were charged in error, email support@formasound.com within 14 days of purchase and we’ll make it right.',
  },
]

export default function Help() {
  const { site } = useShared()
  const email = site.support_email

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }

  return (
    <>
      <Seo
        title="Help & Support"
        description="Get help with your Forma Sound preset packs — how to install .SerumPack files in Serum 2, re-downloads, requirements, and refunds."
        jsonLd={faqLd}
      />

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
        <div className="mono-label mb-3">Support</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">How can we help?</h1>
        <p className="mt-4 text-paper/80 leading-relaxed">
          Answers to the common questions below. Anything else, email{' '}
          <a href={`mailto:${email}`} className="text-paper underline underline-offset-4 hover:opacity-80">
            {email}
          </a>{' '}
          and a human will get back to you.
        </p>

        {/* Install guide */}
        <section className="mt-12 border border-line">
          <div className="border-b border-line px-5 sm:px-6 py-4">
            <div className="mono-label">Guide</div>
            <h2 className="text-xl font-medium mt-1">Installing a preset pack in Serum 2</h2>
          </div>
          <ol className="divide-y divide-line">
            {INSTALL_STEPS.map((step, i) => (
              <li key={i} className="flex gap-4 px-5 sm:px-6 py-4">
                <span className="mono-label !text-paper tabular-nums shrink-0 pt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-paper/90 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <div className="px-5 sm:px-6 py-4 border-t border-line text-sm text-muted">
            Tip: make sure Serum 2 is updated to the latest version — older builds may not recognize newer
            .SerumPack files.
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="mono-label mb-5">Frequently asked</h2>
          <dl className="border-t border-line">
            {FAQS.map((f) => (
              <div key={f.q} className="border-b border-line py-5">
                <dt className="text-base font-medium">{f.q}</dt>
                <dd className="mt-2 text-paper/75 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Contact */}
        <section className="mt-12 border border-line p-6 sm:p-8 text-center">
          <div className="mono-label">Still need a hand?</div>
          <h2 className="text-2xl font-semibold tracking-tight mt-2">We’re happy to help.</h2>
          <p className="mt-2 text-muted">Reach us directly and we’ll reply as soon as we can.</p>
          <a
            href={`mailto:${email}`}
            className="inline-grid place-items-center mt-6 h-12 px-8 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition"
          >
            Email {email}
          </a>
          <div className="mt-6">
            <Link href="/account" className="mono-label hover:text-paper transition-colors">
              Go to your downloads →
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
